/**
 * /experiments/:id — header with honest completeness, scores, cost and latency,
 * then the items (or the config snapshot). `?item=` opens one item, `?tab=config` shows the snapshot, `?tab=scoring`
 * the scoring history.
 *
 * The ticked rows are this page's own state (they feed "Add to dataset" and
 * "Send to annotation"). Scoring the same answers again is not offered here:
 * to score with other evaluators, duplicate the experiment.
 */

import { useState } from "react";
import { useParams } from "react-router";
import { Box, BoxHeader, CodeBlock } from "@/components/ui/box";
import { EmptyState, QueryState } from "@/components/ui/states";
import { Tabs } from "@/components/ui/tabs";
import { formatJson } from "@/lib/format";
import { useUrlState } from "@/lib/use-url-state";
import { ExperimentHeader } from "./detail/experiment-header";
import { ExperimentScores } from "./detail/experiment-scores";
import { useExperiment } from "./hooks";
import { ItemPanel } from "./items/item-panel";
import { ItemsTable } from "./items/items-table";
import { ScoringHistory } from "./scoring/scoring-history";
import type { ExperimentSummary } from "./types";

type Tab = "items" | "config" | "scoring";
const TABS: Tab[] = ["items", "config", "scoring"];

function ConfigSnapshot({ experiment: e }: { experiment: ExperimentSummary }) {
  return (
    <Box>
      <BoxHeader title="Config snapshot">
        <span className="src">
          {e.config_version ? `version ${e.config_version} · ` : ""}saved when the experiment started
        </span>
      </BoxHeader>
      {e.config_snapshot ? (
        <CodeBlock label="Config snapshot">{formatJson(e.config_snapshot)}</CodeBlock>
      ) : (
        <EmptyState title="No snapshot yet">
          The agent's config is saved here when the experiment starts.
        </EmptyState>
      )}
    </Box>
  );
}

export function ExperimentPage() {
  const { id = "" } = useParams();
  const experiment = useExperiment(id);
  const url = useUrlState();
  const tab = TABS.find((t) => t === url.get("tab")) ?? "items";
  const itemId = url.get("item");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  return (
    <QueryState query={experiment} what="the experiment">
      {experiment.data && (
        <>
          <ExperimentHeader experiment={experiment.data} />
          {experiment.data.status !== "draft" && <ExperimentScores experiment={experiment.data} />}
          <Tabs
            label="Experiment sections"
            value={tab}
            onChange={(t) => url.set({ tab: t === "items" ? null : t })}
            items={[
              { key: "items", label: "Items", count: experiment.data.counts.total },
              { key: "config", label: "Config snapshot" },
              { key: "scoring", label: "Scoring history" },
            ]}
          />
          {tab === "items" && (
            <ItemsTable experiment={experiment.data} selected={selected} onSelect={setSelected} />
          )}
          {tab === "config" && <ConfigSnapshot experiment={experiment.data} />}
          {tab === "scoring" && <ScoringHistory experimentId={experiment.data.id} />}
          {itemId && (
            <ItemPanel experiment={experiment.data} itemId={itemId} onClose={() => url.set({ item: null })} />
          )}
        </>
      )}
    </QueryState>
  );
}
